import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";

function Admin() {
    const { refreshAuth } = useAuth();
    const [tab, setTab] = useState("users");
    const [users, setUsers] = useState([]);
    const [rolesData, setRolesData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionMsg, setActionMsg] = useState(null);

    const loadUsers = useCallback(async () => {
        try {
            const data = await api.adminUsers();
            setUsers(data);
        } catch (err) {
            setError(err.message);
        }
    }, []);

    const loadRoles = useCallback(async () => {
        try {
            const data = await api.adminRoles();
            setRolesData(data);
        } catch (err) {
            setError(err.message);
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        Promise.all([loadUsers(), loadRoles()]).finally(() => setLoading(false));
    }, [loadUsers, loadRoles]);

    const showMsg = (msg) => {
        setActionMsg(msg);
        setTimeout(() => setActionMsg(null), 3000);
    };

    if (loading) return <LoadingSpinner message="Loading admin..." />;
    if (error) return <div className="card" style={{ padding: 24, color: "#ef4444" }}>Error: {error}</div>;

    return (
        <div>
            <div className="page-header">
                <h2>Admin</h2>
                <p>User management and role configuration</p>
            </div>

            {actionMsg && (
                <div style={{ background: "#22c55e", color: "#fff", padding: "8px 16px", borderRadius: 6, marginBottom: 16, fontSize: 14 }}>
                    {actionMsg}
                </div>
            )}

            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                <button
                    className={`btn ${tab === "users" ? "btn-primary" : ""}`}
                    onClick={() => setTab("users")}
                    style={tab !== "users" ? { background: "var(--bg-hover)", color: "var(--text)" } : {}}
                >
                    Users
                </button>
                <button
                    className={`btn ${tab === "permissions" ? "btn-primary" : ""}`}
                    onClick={() => setTab("permissions")}
                    style={tab !== "permissions" ? { background: "var(--bg-hover)", color: "var(--text)" } : {}}
                >
                    Permission Matrix
                </button>
            </div>

            {tab === "users" && (
                <UsersTab
                    users={users}
                    roles={rolesData?.roles || []}
                    onReload={() => { loadUsers(); refreshAuth(); }}
                    showMsg={showMsg}
                />
            )}

            {tab === "permissions" && rolesData && (
                <PermissionMatrixTab
                    rolesData={rolesData}
                    onReload={() => { loadRoles(); refreshAuth(); }}
                    showMsg={showMsg}
                />
            )}
        </div>
    );
}

function UsersTab({ users, roles, onReload, showMsg }) {
    const [assigningUser, setAssigningUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState("");
    const [scopeType, setScopeType] = useState("");
    const [scopeValue, setScopeValue] = useState("");
    const [filter, setFilter] = useState("");
    const [showAddUser, setShowAddUser] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [newRole, setNewRole] = useState("");
    const [newScopeType, setNewScopeType] = useState("");
    const [newScopeValue, setNewScopeValue] = useState("");
    const [adding, setAdding] = useState(false);

    const handlePreAdd = async () => {
        if (!newEmail || !newRole) return;
        setAdding(true);
        try {
            await api.adminPreAddUser({
                email: newEmail.trim().toLowerCase(),
                roleId: parseInt(newRole),
                scopeType: newScopeType || null,
                scopeValue: newScopeValue || null,
            });
            showMsg(`Pre-added ${newEmail} — role will activate on first login`);
            setShowAddUser(false);
            setNewEmail("");
            setNewRole("");
            setNewScopeType("");
            setNewScopeValue("");
            onReload();
        } catch (err) {
            showMsg(`Error: ${err.message}`);
        } finally {
            setAdding(false);
        }
    };

    const filtered = users.filter((u) => {
        if (!filter) return true;
        const q = filter.toLowerCase();
        return (
            (u.display_name || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q) ||
            (u.roles || []).some((r) => r.role_key.toLowerCase().includes(q))
        );
    });

    const handleAssign = async () => {
        if (!selectedRole || !assigningUser) return;
        try {
            await api.adminAssignRole(assigningUser.id, {
                roleId: parseInt(selectedRole),
                scopeType: scopeType || null,
                scopeValue: scopeValue || null,
            });
            showMsg(`Role assigned to ${assigningUser.display_name}`);
            setAssigningUser(null);
            setSelectedRole("");
            setScopeType("");
            setScopeValue("");
            onReload();
        } catch (err) {
            showMsg(`Error: ${err.message}`);
        }
    };

    const handleRemoveRole = async (userId, assignmentId, userName) => {
        if (!window.confirm(`Remove this role from ${userName}?`)) return;
        try {
            await api.adminRemoveRole(userId, assignmentId);
            showMsg("Role removed");
            onReload();
        } catch (err) {
            showMsg(`Error: ${err.message}`);
        }
    };

    const handleToggleActive = async (user) => {
        const newState = !user.is_active;
        if (!window.confirm(`${newState ? "Activate" : "Deactivate"} ${user.display_name}?`)) return;
        try {
            await api.adminToggleActive(user.id, newState);
            showMsg(`${user.display_name} ${newState ? "activated" : "deactivated"}`);
            onReload();
        } catch (err) {
            showMsg(`Error: ${err.message}`);
        }
    };

    return (
        <>
            <div style={{ marginBottom: 16, display: "flex", gap: 10, alignItems: "center" }}>
                <input
                    type="text"
                    placeholder="Search users..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    style={{
                        padding: "8px 12px", borderRadius: 6, border: "1px solid var(--border)",
                        background: "var(--bg-card)", color: "var(--text)", width: 300,
                    }}
                />
                <button
                    className="btn btn-primary"
                    style={{ fontSize: 13 }}
                    onClick={() => setShowAddUser(!showAddUser)}
                >
                    + Add User
                </button>
            </div>

            {showAddUser && (
                <div className="card" style={{ padding: 20, marginBottom: 16, border: "2px solid var(--accent)" }}>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>Pre-add user by email</div>
                    <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
                        The user's name will be filled in automatically when they first log in.
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <input
                            type="email"
                            placeholder="email@unitedpetroleum.com.au"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text)", width: 280 }}
                        />
                        <select
                            value={newRole}
                            onChange={(e) => setNewRole(e.target.value)}
                            style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text)" }}
                        >
                            <option value="">Select role...</option>
                            {roles.map((r) => (
                                <option key={r.id} value={r.id}>{r.display_name}</option>
                            ))}
                        </select>
                        <select
                            value={newScopeType}
                            onChange={(e) => setNewScopeType(e.target.value)}
                            style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text)" }}
                        >
                            <option value="">Scope (default)</option>
                            <option value="national">National</option>
                            <option value="state">State</option>
                            <option value="rpl">RPL</option>
                            <option value="site">Site</option>
                        </select>
                        {(newScopeType === "state" || newScopeType === "rpl" || newScopeType === "site") && (
                            <input
                                type="text"
                                placeholder={newScopeType === "state" ? "e.g. VIC" : newScopeType === "rpl" ? "RPL name" : "Site IDs (comma-sep)"}
                                value={newScopeValue}
                                onChange={(e) => setNewScopeValue(e.target.value)}
                                style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text)", width: 180 }}
                            />
                        )}
                        <button className="btn btn-primary" onClick={handlePreAdd} disabled={!newEmail || !newRole || adding}>
                            {adding ? "Adding..." : "Add"}
                        </button>
                        <button className="btn" style={{ background: "var(--bg-hover)" }} onClick={() => setShowAddUser(false)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Assign role modal */}
            {assigningUser && (
                <div className="card" style={{ padding: 20, marginBottom: 16, border: "2px solid var(--accent)" }}>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>
                        Assign role to {assigningUser.display_name}
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text)" }}
                        >
                            <option value="">Select role...</option>
                            {roles.map((r) => (
                                <option key={r.id} value={r.id}>{r.display_name}</option>
                            ))}
                        </select>
                        <select
                            value={scopeType}
                            onChange={(e) => setScopeType(e.target.value)}
                            style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text)" }}
                        >
                            <option value="">Scope (default)</option>
                            <option value="national">National</option>
                            <option value="state">State</option>
                            <option value="rpl">RPL</option>
                            <option value="site">Site</option>
                        </select>
                        {(scopeType === "state" || scopeType === "rpl" || scopeType === "site") && (
                            <input
                                type="text"
                                placeholder={scopeType === "state" ? "e.g. VIC" : scopeType === "rpl" ? "RPL name" : "Site IDs (comma-sep)"}
                                value={scopeValue}
                                onChange={(e) => setScopeValue(e.target.value)}
                                style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text)", width: 180 }}
                            />
                        )}
                        <button className="btn btn-primary" onClick={handleAssign} disabled={!selectedRole}>
                            Assign
                        </button>
                        <button className="btn" style={{ background: "var(--bg-hover)" }} onClick={() => setAssigningUser(null)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="card" style={{ padding: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                        <tr>
                            <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid var(--border)" }}>Name</th>
                            <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid var(--border)" }}>Email</th>
                            <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid var(--border)" }}>Roles</th>
                            <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid var(--border)" }}>Last Login</th>
                            <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid var(--border)" }}>Status</th>
                            <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid var(--border)" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((user) => (
                            <tr key={user.id}>
                                <td style={{ padding: "6px 10px", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>{user.display_name}</td>
                                <td style={{ padding: "6px 10px", fontSize: 12, color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>{user.email}</td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--border)" }}>
                                    {(user.roles || []).map((r) => (
                                        <span
                                            key={r.assignment_id}
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 3,
                                                background: "var(--accent)", color: "#fff",
                                                padding: "1px 6px", borderRadius: 4, fontSize: 11,
                                                marginRight: 3, marginBottom: 1,
                                            }}
                                        >
                                            {r.role_name}
                                            {r.scope_value && (
                                                <span style={{ opacity: 0.8, fontSize: 10 }}>({r.scope_value})</span>
                                            )}
                                            <button
                                                onClick={() => handleRemoveRole(user.id, r.assignment_id, user.display_name)}
                                                style={{
                                                    background: "none", border: "none", color: "#fff",
                                                    cursor: "pointer", padding: "0 2px", fontSize: 13,
                                                    lineHeight: 1, opacity: 0.7,
                                                }}
                                                title="Remove role"
                                            >
                                                &times;
                                            </button>
                                        </span>
                                    ))}
                                    {(!user.roles || user.roles.length === 0) && (
                                        <span style={{ color: "var(--text-muted)", fontSize: 11 }}>No roles</span>
                                    )}
                                </td>
                                <td style={{ padding: "6px 10px", fontSize: 12, color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    {(user.azure_oid || "").startsWith("pending")
                                        ? <span style={{ color: "#f59e0b", fontWeight: 600, fontSize: 11 }}>Awaiting login</span>
                                        : user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : "\u2014"}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--border)" }}>
                                    <span style={{
                                        color: user.is_active ? "#22c55e" : "#ef4444",
                                        fontWeight: 600, fontSize: 11,
                                    }}>
                                        {user.is_active ? "Active" : "Inactive"}
                                    </span>
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--border)" }}>
                                    <div style={{ display: "flex", gap: 4 }}>
                                        <button
                                            className="btn"
                                            style={{ fontSize: 11, padding: "3px 8px", background: "var(--accent)", color: "#fff" }}
                                            onClick={() => setAssigningUser(user)}
                                        >
                                            + Role
                                        </button>
                                        <button
                                            className="btn"
                                            style={{
                                                fontSize: 11, padding: "3px 8px",
                                                background: user.is_active ? "#ef444420" : "#22c55e20",
                                                color: user.is_active ? "#ef4444" : "#22c55e",
                                            }}
                                            onClick={() => handleToggleActive(user)}
                                        >
                                            {user.is_active ? "Deactivate" : "Activate"}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filtered.length === 0 && (
                    <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted)" }}>
                        {filter ? "No users match your search" : "No users found"}
                    </div>
                )}
            </div>
        </>
    );
}

function PermissionMatrixTab({ rolesData, onReload, showMsg }) {
    const { roles, permissions, matrix } = rolesData;
    const [localMatrix, setLocalMatrix] = useState(() => {
        const m = {};
        for (const entry of matrix) {
            const key = `${entry.role_id}-${entry.permission_id}`;
            m[key] = true;
        }
        return m;
    });
    const [saving, setSaving] = useState(null);

    // Group permissions by category
    const categories = {};
    for (const p of permissions) {
        if (!categories[p.category]) categories[p.category] = [];
        categories[p.category].push(p);
    }

    const toggle = (roleId, permId) => {
        const key = `${roleId}-${permId}`;
        setLocalMatrix((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const saveRole = async (role) => {
        setSaving(role.id);
        try {
            const permIds = permissions
                .filter((p) => localMatrix[`${role.id}-${p.id}`])
                .map((p) => p.id);
            await api.adminUpdateRole(role.id, permIds);
            showMsg(`Saved permissions for ${role.display_name}`);
            onReload();
        } catch (err) {
            showMsg(`Error: ${err.message}`);
        } finally {
            setSaving(null);
        }
    };

    const compactTd = { padding: "4px 8px" };
    const compactTh = { padding: "6px 8px" };

    return (
        <div className="card" style={{ padding: 12, overflowX: "auto", maxWidth: "calc(100vw - 48px)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                    <tr>
                        <th style={{ ...compactTh, position: "sticky", left: 0, background: "var(--bg-card)", zIndex: 2, textAlign: "left", fontWeight: 600, color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                            Permission
                        </th>
                        {roles.map((r) => (
                            <th key={r.id} style={{ ...compactTh, textAlign: "center", fontWeight: 600, color: "var(--text-muted)", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>
                                {r.display_name}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(categories).map(([cat, perms]) => (
                        <React.Fragment key={cat}>
                            <tr>
                                <td
                                    colSpan={roles.length + 1}
                                    style={{
                                        fontWeight: 700, fontSize: 11, textTransform: "uppercase",
                                        color: "var(--text-muted)", padding: "8px 8px 3px",
                                        background: "var(--bg-hover)", letterSpacing: 0.5,
                                    }}
                                >
                                    {cat}
                                </td>
                            </tr>
                            {perms.map((perm) => (
                                <tr key={perm.id}>
                                    <td style={{
                                        ...compactTd, position: "sticky", left: 0, background: "var(--bg-card)",
                                        zIndex: 1, fontWeight: 500, borderBottom: "1px solid var(--border)",
                                    }}>
                                        {perm.display_name}
                                    </td>
                                    {roles.map((role) => (
                                        <td key={role.id} style={{ ...compactTd, textAlign: "center", borderBottom: "1px solid var(--border)" }}>
                                            <input
                                                type="checkbox"
                                                checked={!!localMatrix[`${role.id}-${perm.id}`]}
                                                onChange={() => toggle(role.id, perm.id)}
                                                style={{ cursor: "pointer", width: 15, height: 15 }}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            <div style={{ padding: "12px 0 0", display: "flex", gap: 8, flexWrap: "wrap" }}>
                {roles.map((role) => (
                    <button
                        key={role.id}
                        className="btn btn-primary"
                        style={{ fontSize: 12, padding: "6px 14px" }}
                        onClick={() => saveRole(role)}
                        disabled={saving === role.id}
                    >
                        {saving === role.id ? "Saving..." : `Save ${role.display_name}`}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default Admin;
